// Visitor & Contractor module — API layer (RON-5466).
//
// THIS IS THE SWAP POINT. Every function returns the app's standard response envelope
// (`{ Data: { ResponseCode, Records } }`) and is `async`, exactly like the real Layer A
// wrappers. While `USE_VISITOR_MOCKS` is true the data comes from `visitorMockData.js`.
//
// When the endpoints exist, each body becomes the usual three lines and NOTHING in any screen
// changes:
//
//   export const GetVisits_api = (requestParams) => {
//     requestParams.AppVersion = AppConstant.AppVersion;
//     requestParams.Language = AppConstant.Language;
//     requestParams.DeviceId = AppConstant.DeviceID;
//     requestParams.OsVersion = AppConstant.OsVersion;
//     requestParams.ManufactureModel = AppConstant.ManufactureModel;
//     return post({ url: `${BASE_URL}${GetVisits_url}`, params: requestParams });
//   };
//
// The paths then go in `src/config/ApiConfig.js` alongside the other endpoints.

import {
  CONTRACTOR_DB,
  MOCK_HOST_PERMISSIONS,
  MOCK_SITES,
  MOCK_VISITS,
  USE_VISITOR_MOCKS,
} from "./visitorMockData.js";
import {
  VISIT_STATUS_FILTERS,
  contractorListFromApi,
  hostPermissionsFromApi,
  createResultFromApi,
  createVisitToApi,
  groupCancelFromApi,
  patchVisitToApi,
  siteListFromApi,
  visitListFromApi,
  visitsQueryToApi,
} from "./visitorApiMapper.js";
import { VISIT_STATUS } from "./visitorConstants.js";
import { post } from "../../api/HttpClient.js";
import {
  BASE_URL,
  VisitorContractors_url,
  VisitorHostPermissions_url,
  VisitorGroupCancel_url,
  VisitorSites_url,
  VisitorVisitCancel_url,
  VisitorVisits_url,
  VisitorVisit_url,
} from "../../config/ApiConfig.js";

// Small delay so loading states are actually exercised during design review instead of
// resolving in the same tick and never appearing.
const MOCK_LATENCY_MS = 300;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Matches the shape every screen already expects from `post(...)`.
const mockResponse = (records) => ({
  Data: {
    ResponseCode: 200,
    Records: records,
  },
});

// Failure envelope, same shape `handleApiUiResponse` reads. Codes in ORANGE_POPUP_CODES (409 here)
// surface as the app's alert popup rather than an error toast.
const mockFailure = (code, message) => ({
  Data: {
    ResponseCode: code,
    ErrorMessage: message,
  },
});

// ── Capabilities: the SERVER's answer, not the client's ──────────────────────────────────────
//
// RON-6002 §4.5: "`can_edit` and `can_cancel` are the server's answer. Use them. Do not derive
// them from `status`." The real API computes them from *status is `expected`* AND *you hold the
// capability for that kind of visit*, and sends them on every row.
//
// This function is the MOCK SERVER doing that job. It lives here — not in `visitorConstants.js`
// where it used to — precisely because it is server work: when `USE_VISITOR_MOCKS` goes false
// this function is deleted and `visitFromApi` reads the real flags instead. Nothing in any screen
// changes, because no screen derives anything any more.
//
// ONLY AN UPCOMING VISIT CAN BE CANCELLED (Q5, decided 23-Sep-2026 in favour of the contract).
//
// This module previously let a host cancel a guest who had already arrived, and treated that as
// also checking them out. The contract refuses it in two places — §4.5's rule for `can_cancel` is
// *status is `expected`*, and §4.8 answers "409 if already cancelled or the visitor has arrived" —
// so the real server would have refused an action the UI offered, and the host would have met a
// 409 popup on a button that looked live.
//
// A guest who is on site gets checked out, not cancelled, and that is reception's job.
const CANCELLABLE_STATUSES = [VISIT_STATUS.EXPECTED];

const deriveCapabilities = (visit) => ({
  // WIRE names, because the fixture is now wire format and `visitFromApi` reads these keys from
  // the mock exactly as it reads them from the server.
  can_edit: visit?.status === VISIT_STATUS.EXPECTED,
  can_cancel: CANCELLABLE_STATUSES.includes(visit?.status),
});

// Every row leaving this module carries its capabilities, exactly as the real API's rows do.
const withCapabilities = (visit) =>
  visit == null ? visit : { ...visit, ...deriveCapabilities(visit) };

// Deep-ish clone so a screen mutating a row cannot corrupt the fixture for the next read —
// the real API would hand back fresh objects each call. Capabilities are stamped on the way out,
// so a status the store changed (a cancel) yields fresh flags rather than stale ones.
const cloneVisits = (visits) => visits.map(withCapabilities);

// Seeding is NOT stamped: the store holds raw rows and capabilities are computed at every read, so
// a cancel that changes `status` cannot leave a stale `canCancel: true` behind it.
const seedVisits = (visits) => visits.map((visit) => ({ ...visit }));

// ── Session store ────────────────────────────────────────────────────────────────
// Edit and cancel MUST persist for the length of the session. `CreateVisit_api` deliberately does
// not (see its note) because a new row has no visible home in a fixed fixture, but a cancel that
// leaves the row reading "Expected" in the list makes the flow look broken in design review.
//
// The store is a copy, so `visitorMockData.js` stays a pure fixture and a page reload resets
// everything. It re-seeds whenever the fixture's identity changes, which is what lets a test swap
// `MOCK_VISITS` between cases without needing a reset hook.
let visitStore = null;
let seededFrom = null;

const store = () => {
  if (visitStore === null || seededFrom !== MOCK_VISITS) {
    visitStore = seedVisits(MOCK_VISITS);
    seededFrom = MOCK_VISITS;
  }

  return visitStore;
};

// Accepts `VisitIDs: [...]`, a single `VisitID`, or a `GroupID`, so one function serves the single,
// group and bulk flows — and one real endpoint replaces it.
const resolveTargets = ({ VisitIDs, VisitID, GroupID }) => {
  if (GroupID) return store().filter((visit) => visit.group_id === GroupID);

  const ids = VisitIDs ?? (VisitID == null ? [] : [VisitID]);
  const wanted = new Set(Array.isArray(ids) ? ids : [ids]);

  return store().filter((visit) => wanted.has(visit.id));
};

const applyToStore = (visit, changes) => {
  const index = store().findIndex((candidate) => candidate.id === visit.id);
  if (index === -1) return null;

  // `applyPatch` rather than a spread, because the patch may carry dotted paths: a visitor's name
  // lives under `visitor` on the wire, and a flat spread would write a stray `"visitor.name"` key
  // that nothing reads.
  store()[index] = applyPatch(store()[index], changes);

  // Stamped on the way out, like every other read: a cancel changes `status`, so the row the
  // caller gets back must carry the NEW capabilities, not the ones it had a moment ago.
  return withCapabilities(store()[index]);
};

// Only the fields a caller actually sent. A bulk edit sends two or three keys and must not blank
// everything else, so `undefined` means "leave alone" rather than "clear".
// Request param → the WIRE field it writes. The store now holds wire-format rows, so a patch that
// wrote `arrivalTime` would quietly add a second field beside `arrival_time` and change nothing
// anybody reads.
//
// Three of these are NESTED on the wire — a visitor's name, email and company live under
// `visitor`, and the host under `host` — so they are spelled as paths and applied below.
const FIELD_MAP = {
  Name: "visitor.name",
  Email: "visitor.email",
  Company: "visitor.company",
  StartDate: "start_date",
  EndDate: "end_date",
  ArrivalTime: "arrival_time",
  Site: "site.name",
  Building: "building.name",
  HostName: "host.name",
  Type: "type",
};

const buildPatch = (requestParams) =>
  Object.entries(FIELD_MAP).reduce((patch, [param, field]) => {
    if (requestParams[param] !== undefined) patch[field] = requestParams[param];
    return patch;
  }, {});

// Writes a dotted path, creating the intermediate object if the row has none — a visit with no
// building yet still has to accept one being set.
const applyPatch = (row, patch) => {
  const next = { ...row };

  Object.entries(patch).forEach(([path, value]) => {
    const [head, tail] = path.split(".");

    if (tail === undefined) {
      next[head] = value;
      return;
    }

    next[head] = { ...(next[head] || {}), [tail]: value };
  });

  return next;
};

// `handleApiUiResponse` shows the right toast/popup but returns nothing, so callers that need to
// branch on the outcome (e.g. only advance a wizard on success) must read the code themselves.
// 200 is the single success code that helper treats as success.
export const isSuccessfulResponse = (response) =>
  Number(response?.Data?.ResponseCode) === 200;

// ── The live transport ───────────────────────────────────────────────────────────
//
// §6: send `Idempotency-Key` on endpoints 6, 7, 8 and 9 — "a UUID you generate per user action
// and reuse on retry. These clients are a phone on a train and a laptop on hotel wifi. Without a
// key, a lost response and a tapped 'try again' invites the party twice."
//
// One helper for every write, so it cannot be forgotten on one of them. It is a HEADER, not a
// body field — that is where the server reads it. `post` takes an optional `headers` for exactly
// this, and ours survives the token-refresh wrapper's 401 retry, which is what the contract wants:
// a retry reuses the same key rather than minting a new one.
const liveWrite = ({ url, method = "POST", params = {}, idempotencyKey }) =>
  post({
    url: `${BASE_URL}${url}`,
    method,
    params,
    headers: idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {},
  });

// A GET with its query in the URL. `post` sends no body for GET.
const liveRead = (url, query = {}) => {
  const search = new URLSearchParams(
    Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ).toString();

  return post({ url: `${BASE_URL}${url}${search ? `?${search}` : ""}`, method: "GET" });
};

const notWired = (name) => {
  throw new Error(
    `Visitor APIs are not wired yet (${name}). Set USE_VISITOR_MOCKS = true in ` +
      "visitorMockData.js, or implement this function against the real endpoint."
  );
};

// GET the visits this employee hosts.
// Real endpoint TBD — the natural name is `GetVisits_api` / `MyVisits_api`.
// The list is PAGINATED and SEARCHED SERVER-SIDE (§4.4): `?q=&status=&page=&per_page=`.
//
// Both matter together. Once the list only holds one page, a client-side search would look through
// the rows already fetched and report "no visitors" for someone sitting on page 3 — which is the
// same lie §4.4 warns about for a mistyped status. So the term goes to the server.
//
// This mock does the filtering and slicing the real endpoint will do, rather than returning
// everything and letting the screen pretend, so the lazy-loading and empty states are exercised
// for real in design review.
export const DEFAULT_PAGE_SIZE = 25;

export const GetVisits_api = async (requestParams = {}) => {
  // Validated on BOTH paths. §4.4 is blunt about why: "a misspelled status is a 422, not an empty
  // list — an empty list on screen reads as 'you have no visitors' when the truth is a typo." So
  // a bad filter is refused before the request leaves, and the mock refuses it identically.
  if (!USE_VISITOR_MOCKS) {
    return liveRead(VisitorVisits_url, visitsQueryToApi(requestParams));
  }

  await delay(MOCK_LATENCY_MS);

  const page = Math.max(1, Number(requestParams.Page) || 1);
  const perPage = Math.max(1, Number(requestParams.PerPage) || DEFAULT_PAGE_SIZE);
  const term = String(requestParams.Query || "").trim().toLowerCase();
  const status = requestParams.Status || null;

  // §4.4 lists exactly five values and a wrong one is a 422, never an empty list — an empty list
  // reads as "you have no visitors" when the truth is a typo.
  if (status && !VISIT_STATUS_FILTERS.includes(status)) {
    return mockFailure(422, `Unknown status filter "${status}".`);
  }

  const matches = store().filter((visit) => {
    if (status && visit.status !== status) return false;
    if (!term) return true;

    // Name, company and the raw status, matching what the search placeholder promises. The real
    // endpoint searches name, email and company server-side.
    return [visit.visitor?.name, visit.visitor?.company, visit.visitor?.email, visit.status]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term));
  });

  const start = (page - 1) * perPage;

  return mockResponse({
    visits: cloneVisits(matches.slice(start, start + perPage)),
    // The count of everything that matched, NOT of this page — it is what tells the screen
    // whether there is more to load.
    total: matches.length,
    page,
    per_page: perPage,
  });
};

// GET the sites (and their buildings) this employee may invite into.
// Real endpoint TBD. Scoped by CompanyID server-side; only `active` sites should come back.
export const GetVisitorSites_api = async () => {
  if (!USE_VISITOR_MOCKS) return liveRead(VisitorSites_url);

  await delay(MOCK_LATENCY_MS);

  // `sites`, matching §4.2 — the fixture is already wire format, so `siteListFromApi` does the
  // same work here as it does on the live answer.
  return mockResponse({
    sites: MOCK_SITES.map((site) => ({
      ...site,
      buildings: site.buildings.map((building) => ({ ...building })),
    })),
  });
};

// POST a new visit (one row per invited visitor, sharing a `group`).
//
// While mocked this only echoes back what it was handed, with server-assigned fields filled in —
// it does NOT mutate MOCK_VISITS, so the fixture stays stable for the next page load. The real
// endpoint will persist and the list will re-fetch.
export const CreateVisit_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) {
    return liveWrite({
      url: VisitorVisits_url,
      params: createVisitToApi(requestParams),
      idempotencyKey: requestParams.IdempotencyKey,
    });
  }

  await delay(MOCK_LATENCY_MS);

  const visitors = Array.isArray(requestParams.Visitors) ? requestParams.Visitors : [];

  return mockResponse({
    // The real API assigns these; the confirmation screen reads them back. Returned in WIRE
    // shape (§4.6), so the confirmation goes through `createResultFromApi` on both paths.
    visits: visitors.map((visitor, index) => ({
      id: `v-new-${index}`,
      booking_code: `VIS-NEW-${String(index + 1).padStart(3, "0")}`,
      kind: visitor.kind ?? "visitor",
      type: visitor.type ?? "standard",
      status: "expected",
      visitor: {
        name: visitor.name,
        email: visitor.email,
        company: visitor.company ?? null,
      },
      start_date: requestParams.StartDate ?? null,
      end_date: requestParams.EndDate ?? requestParams.StartDate ?? null,
      arrival_time: requestParams.ArrivalTime ?? null,
      site: { id: requestParams.SiteID ?? null, name: null, timezone: null },
      building: null,
      group_id: "g-new",
      host: { name: null },
      // The real API sends capabilities on every row it returns, a freshly created one included.
      can_edit: true,
      can_cancel: true,
    })),
    group_id: "g-new",
  });
};

// PATCH one or more existing visits.
//
// One function for all three edit flows — single (`VisitID`), group (`GroupID`) and bulk
// (`VisitIDs`) — because they differ only in which rows they target. The real endpoint should keep
// that shape so bulk edit stays one round trip instead of N.
//
// Only the fields present in `requestParams` are written; see `buildPatch`.
export const UpdateVisit_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) {
    // §4.7 patches ONE visit. The group and bulk flows are this call repeated, which the wrapper
    // below does — the mock serves all three in one pass only because it is a fixture.
    return liveWrite({
      url: VisitorVisit_url(requestParams.VisitID),
      method: "PATCH",
      params: patchVisitToApi(requestParams),
      idempotencyKey: requestParams.IdempotencyKey,
    });
  }

  await delay(MOCK_LATENCY_MS);

  const targets = resolveTargets(requestParams);

  if (targets.length === 0) {
    return mockFailure(404, "No matching visit was found.");
  }

  const patch = buildPatch(requestParams);
  const updated = targets
    .map((visit) => applyToStore(visit, patch))
    .filter(Boolean);

  return mockResponse({ visits: updated, updated_count: updated.length });
};

// Cancel one or more visits.
//
// ONLY AN UPCOMING VISIT (Q5, 23-Sep-2026). A guest who has already arrived is checked out by
// reception, not cancelled by their host — §4.8 answers 409 once the visitor has arrived, so
// offering it would have meant a 409 popup on a button that looked live.
//
// Rows that cannot be cancelled (already cancelled, checked out, no-show, arrived) are skipped
// rather than
// erroring the whole batch: a bulk cancel over a mixed selection should still do the work it can.
// If nothing at all was cancellable that IS an error, otherwise the UI would report success for a
// no-op.
export const CancelVisit_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) {
    // TWO ENDPOINTS, picked by what the caller asked for. §4.9's group cancel is PARTIAL by
    // design — "2 cancelled, 1 already checked in" — which is why it is a different call rather
    // than a loop: the server decides what it could do, and says what it skipped.
    // §8/§9, 24-October-2026 — the "Send cancellation email" toggle. DEFAULT TRUE on the server,
    // so the flag rides only when the host turned it off. Both endpoints take it.
    const params =
      requestParams.SendCancellationEmail === false ? { send_cancellation: false } : {};

    if (requestParams.GroupID) {
      return liveWrite({
        url: VisitorGroupCancel_url(requestParams.GroupID),
        params,
        idempotencyKey: requestParams.IdempotencyKey,
      });
    }

    return liveWrite({
      url: VisitorVisitCancel_url(requestParams.VisitID),
      params,
      idempotencyKey: requestParams.IdempotencyKey,
    });
  }

  await delay(MOCK_LATENCY_MS);

  const targets = resolveTargets(requestParams);

  if (targets.length === 0) {
    return mockFailure(404, "No matching visit was found.");
  }

  // The SERVER decides this, so it derives the answer itself rather than calling the screens'
  // predicate — that predicate now just reads the flag this function is about to stamp.
  const cancellable = targets.filter((visit) => deriveCapabilities(visit).can_cancel);

  if (cancellable.length === 0) {
    // 409 is in ORANGE_POPUP_CODES, so this surfaces as the app's alert popup.
    return mockFailure(409, "These visits can no longer be cancelled.");
  }

  const cancelled = cancellable
    .map((visit) => applyToStore(visit, { status: VISIT_STATUS.CANCELLED }))
    .filter(Boolean);

  // One email per visitor ACTUALLY cancelled, and only those with an address on the visit — §4.9.
  // Counted from `cancelled` rather than `targets`, because the skipped ones are not emailed.
  const wantsEmail = requestParams.SendCancellationEmail !== false;
  const emailed = wantsEmail ? cancelled.filter((visit) => visit?.visitor?.email).length : 0;

  return mockResponse({
    visits: cancelled,
    cancelled: cancelled.length,
    // Rows the caller asked for but that were already closed, or whose guest has arrived. §4.9
    // reports the same thing as `skipped`, so a mixed selection still does the work it can and
    // says what it left alone.
    skipped: targets.length - cancellable.length,
    emailed,
    // The SINGLE cancel's three-state answer (§4.8). `null` when the host turned the toggle off,
    // `false` when it was on but there was no address to send to.
    email_sent: requestParams.GroupID ? undefined : !wantsEmail ? null : emailed > 0,
  });
};

// ── Contractor vetting (Phase 4) ─────────────────────────────────────────────────

// Look a contractor up in the vetting register by email.
//
// A MISS IS NOT AN ERROR. "This person is not on the register" is the single most common outcome
// for a host inviting someone new, and it is the entry point to the request-vetting flow — so it
// returns 200 with `Found: false` rather than a 404 the screen would have to translate back into
// a normal state. Only a malformed request is an error.
//
// Email is matched case-insensitively and trimmed: hosts paste addresses out of Outlook, and
// "Declan@Murphyelectrical.ie " must find the same record.
export const LookupContractor_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) {
    // There is no lookup-by-email endpoint. The register (§4.3) is searchable, so an exact email
    // is handed to `?q=` and the caller below checks whether what came back actually matches —
    // a search that returns a near miss must not read as "found".
    return liveRead(VisitorContractors_url, { q: requestParams.Email });
  }

  await delay(MOCK_LATENCY_MS);

  const email = String(requestParams.Email || "").trim().toLowerCase();

  if (!email) return mockFailure(400, "An email address is required.");

  const record = CONTRACTOR_DB.find(
    (candidate) => candidate.email.toLowerCase() === email
  );

  if (!record) return mockResponse({ Found: false, Contractor: null });

  return mockResponse({
    Found: true,
    // Cloned, documents included, so a screen cannot mutate the register.
    Contractor: {
      ...record,
      documents: (record.documents || []).map((document) => ({ ...document })),
    },
  });
};

// Ask facilities to vet someone who is not on the register yet.
//
// This does NOT create a visit. Vetting is a separate approval that has to complete first, so the
// host gets a "request sent" confirmation and comes back to book once it clears. Conflating the
// two would let an unvetted contractor onto a site by accident.
export const RequestContractorVetting_api = async (requestParams = {}) => {
  // NO ENDPOINT EXISTS FOR THIS. RON-6002 has nine endpoints and none of them requests vetting —
  // §5 describes the three outcomes of booking a contractor who is already on the register, not a
  // way to put somebody on it. Raised with the Laravel team; see docs/RON-6002_open-questions.md.
  //
  // It throws rather than quietly returning a success the server never gave, because the host
  // would otherwise be told "request sent" and wait for a vetting nobody was ever asked for. The
  // screen behind it is gated instead, so in practice this is unreachable — the throw is the
  // backstop for the day somebody removes the gate.
  if (!USE_VISITOR_MOCKS) notWired("RequestContractorVetting_api");

  await delay(MOCK_LATENCY_MS);

  const email = String(requestParams.Email || "").trim().toLowerCase();

  if (!email) return mockFailure(400, "An email address is required.");

  const existing = CONTRACTOR_DB.find(
    (candidate) => candidate.email.toLowerCase() === email
  );

  // 409 is in ORANGE_POPUP_CODES, so this surfaces as the app's alert popup rather than a toast.
  if (existing) {
    return mockFailure(409, "This contractor is already on the vetting register.");
  }

  return mockResponse({
    RequestId: "vet-req-new",
    Email: requestParams.Email,
    // What the host is waiting on. The real endpoint should return the same so the confirmation
    // can say who to chase.
    Status: "pending",
  });
};

// ── Mapped wrappers ──────────────────────────────────────────────────────────────
//
// THE ONLY THING SCREENS SHOULD CALL. Each unwraps the envelope and runs the payload through
// `visitorApiMapper`, so a screen sees one flat camelCase shape whichever path answered.
//
// Added 1-October-2026, with the fixtures. Before that the mapper existed but nothing called it:
// the screens read the mock's own keys directly, which meant the translation layer was never
// exercised and `Data.Records` was unwrapped in half a dozen places. Now it happens once, here.
export const fetchVisits = async (requestParams = {}) => {
  const response = await GetVisits_api(requestParams);

  if (!isSuccessfulResponse(response)) return { ok: false, response, list: null };

  return { ok: true, response, list: visitListFromApi(response.Data.Records) };
};

export const fetchVisitorSites = async () => {
  const response = await GetVisitorSites_api();

  if (!isSuccessfulResponse(response)) return { ok: false, response, sites: [] };

  return { ok: true, response, sites: siteListFromApi(response.Data.Records) };
};

export const createVisit = async (requestParams = {}) => {
  const response = await CreateVisit_api(requestParams);

  if (!isSuccessfulResponse(response)) return { ok: false, response, result: null };

  return { ok: true, response, result: createResultFromApi(response.Data.Records) };
};

export const updateVisit = async (requestParams = {}) => {
  const response = await UpdateVisit_api(requestParams);

  if (!isSuccessfulResponse(response)) return { ok: false, response, list: null, emailSent: null };

  // §7, 24-October-2026: the reply is the visit, plus `email_sent`. Read from the envelope rather
  // than from the row, because it describes what the REQUEST did, not what the visit is — a
  // second identical PATCH would answer `null` for the same unchanged visit.
  //
  // THREE-STATE. See `groupCancelFromApi` for why it must not become a boolean.
  return {
    ok: true,
    response,
    list: visitListFromApi(response.Data.Records),
    emailSent: response.Data.Records?.email_sent ?? null,
  };
};

// Group cancels report PARTIAL success (§4.9) — "2 cancelled, 1 already checked in" — so the
// counts are mapped rather than the outcome being reduced to a boolean. A single cancel answers
// the same shape with one row, so callers read one result either way.
export const cancelVisit = async (requestParams = {}) => {
  const response = await CancelVisit_api(requestParams);

  if (!isSuccessfulResponse(response)) return { ok: false, response, result: null };

  // `groupCancelFromApi` reads both shapes: `cancelled`/`skipped`/`emailed` from a group cancel
  // (§4.9) and `email_sent` from a single one (§4.8). Whichever the server sent is the one the
  // caller reads; the other stays at its default.
  return { ok: true, response, result: groupCancelFromApi(response.Data.Records) };
};

// The register has no lookup-by-email, so an exact address goes to `?q=` and the MATCH IS CHECKED
// HERE. A search that returned a near miss — a colleague at the same firm, a similar address —
// must never read as "this contractor is on the register", because the answer decides whether the
// wizard books them or sends the host down the vetting path.
export const lookupContractor = async (requestParams = {}) => {
  const response = await LookupContractor_api(requestParams);

  if (!isSuccessfulResponse(response)) return { ok: false, response, contractor: null };

  const records = response.Data.Records;
  const wanted = String(requestParams.Email || "").trim().toLowerCase();

  // The mock still answers the older `Found`/`Contractor` shape; the live path answers the
  // register's list. Both are reduced to the same question: is this exact person on it?
  if (records?.Contractor || records?.Found !== undefined) {
    const found = records.Found === true ? records.Contractor : null;

    return { ok: true, response, contractor: found };
  }

  const { contractors } = contractorListFromApi(records);
  const exact = contractors.find(
    (candidate) => String(candidate.email || "").toLowerCase() === wanted
  );

  return { ok: true, response, contractor: exact || null };
};

// `GET /visitor/me/host-permissions` (§4.1) — the five rights, plus the server's own `can_invite`.
//
// Its own function rather than part of the list read, because `useVisitorAccess` needs it before
// any screen mounts: it is what decides whether the module appears in the sidebar at all.
export const GetHostPermissions_api = async () => {
  if (!USE_VISITOR_MOCKS) return liveRead(VisitorHostPermissions_url);

  await delay(MOCK_LATENCY_MS);

  // Wire format, so `hostPermissionsFromApi` runs on both paths like everything else here.
  return mockResponse({
    capabilities: {
      visitors: MOCK_HOST_PERMISSIONS.canVisitor,
      contractors: MOCK_HOST_PERMISSIONS.canContractor,
      parking: MOCK_HOST_PERMISSIONS.canParking,
      desks: MOCK_HOST_PERMISSIONS.canDesk,
      reception: MOCK_HOST_PERMISSIONS.canReception,
    },
    can_invite: MOCK_HOST_PERMISSIONS.canVisitor || MOCK_HOST_PERMISSIONS.canContractor,
  });
};

export const fetchHostPermissions = async () => {
  const response = await GetHostPermissions_api();

  if (!isSuccessfulResponse(response)) return { ok: false, response, permissions: null };

  return { ok: true, response, permissions: hostPermissionsFromApi(response.Data.Records) };
};
